'use client'

import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

export default function PrivacyPolicyPage() {
  const lastUpdated = 'January 6, 2026'
  const companyEmail = 'privacy@landlordnoagent.com'

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2 text-stone-600 hover:text-emerald-600 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo.png" alt="LandlordNoAgent Logo" className="w-12 h-12 object-contain" />
            <span className="text-lg font-bold text-stone-800">
              Landlord<span className="text-emerald-600">NoAgent</span>
            </span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">Privacy Policy</h1>
          <p className="text-stone-500 mb-8">Last Updated: {lastUpdated}</p>

          <div className="prose prose-stone max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">1. Introduction</h2>
              <p className="text-stone-600 mb-4">
                LandLordNoAgent ("we," "our," "us," or the "Platform") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, mobile applications, and services.
              </p>
              <p className="text-stone-600">
                By using our Services, you consent to the data practices described in this policy. If you do not agree with the terms of this Privacy Policy, please do not access the Services.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-lg font-medium text-stone-700 mb-2">2.1 Personal Information</h3>
              <p className="text-stone-600 mb-4">We may collect the following personal information:</p>
              <ul className="list-disc pl-6 space-y-2 text-stone-600 mb-4">
                <li><strong>Identity Information:</strong> Name, email address, phone number, date of birth</li>
                <li><strong>Account Information:</strong> Username, password (encrypted), profile picture</li>
                <li><strong>KYC Documents:</strong> Government-issued ID, proof of address, bank statements, employment verification</li>
                <li><strong>Financial Information:</strong> Bank account details (for payouts), payment history</li>
                <li><strong>Property Information:</strong> Property addresses, descriptions, images, ownership documents</li>
                <li><strong>Employment Information:</strong> Employer name, occupation, income (for tenant applications)</li>
              </ul>

              <h3 className="text-lg font-medium text-stone-700 mb-2">2.2 Automatically Collected Information</h3>
              <p className="text-stone-600 mb-4">When you use our Services, we automatically collect:</p>
              <ul className="list-disc pl-6 space-y-2 text-stone-600 mb-4">
                <li><strong>Device Information:</strong> Browser type, operating system, device type</li>
                <li><strong>Log Data:</strong> IP address, access times, pages viewed, referring URL</li>
                <li><strong>Location Data:</strong> General geographic location based on IP address</li>
                <li><strong>Cookies and Tracking:</strong> Cookies, pixels, and similar technologies</li>
              </ul>

              <h3 className="text-lg font-medium text-stone-700 mb-2">2.3 Third-Party Information</h3>
              <p className="text-stone-600">
                We may receive information from third parties such as Stripe (payment processing), Cloudinary (image storage), and identity verification services.
              </p>
            </section>

            {/* How We Use Your Information */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">3. How We Use Your Information</h2>
              <p className="text-stone-600 mb-4">We use your information to:</p>
              <ul className="list-disc pl-6 space-y-2 text-stone-600">
                <li><strong>Provide Services:</strong> Create accounts, process applications, facilitate property rentals</li>
                <li><strong>Process Payments:</strong> Handle rent payments, escrow, commissions, and payouts</li>
                <li><strong>Identity Verification:</strong> Verify user identities through KYC processes</li>
                <li><strong>Communication:</strong> Send transactional emails, notifications, and support responses</li>
                <li><strong>Improve Services:</strong> Analyze usage patterns, fix bugs, develop new features</li>
                <li><strong>Security:</strong> Detect fraud, prevent abuse, protect users</li>
                <li><strong>Legal Compliance:</strong> Comply with laws, respond to legal requests</li>
                <li><strong>Marketing:</strong> Send promotional content (with your consent)</li>
              </ul>
            </section>

            {/* Information Sharing */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">4. Information Sharing and Disclosure</h2>
              <p className="text-stone-600 mb-4">We may share your information with:</p>
              
              <h3 className="text-lg font-medium text-stone-700 mb-2">4.1 Other Users</h3>
              <ul className="list-disc pl-6 space-y-2 text-stone-600 mb-4">
                <li>Landlords see tenant application information (name, contact, employment details)</li>
                <li>Tenants see landlord contact information and property details</li>
                <li>Public profiles may display limited information</li>
              </ul>

              <h3 className="text-lg font-medium text-stone-700 mb-2">4.2 Service Providers</h3>
              <ul className="list-disc pl-6 space-y-2 text-stone-600 mb-4">
                <li><strong>Stripe:</strong> Payment processing</li>
                <li><strong>Cloudinary:</strong> Image and document storage</li>
                <li><strong>Resend/Email Services:</strong> Email delivery</li>
                <li><strong>MongoDB Atlas:</strong> Database hosting</li>
                <li><strong>Analytics Providers:</strong> Usage analytics</li>
              </ul>

              <h3 className="text-lg font-medium text-stone-700 mb-2">4.3 Legal Requirements</h3>
              <p className="text-stone-600 mb-4">
                We may disclose information if required by law, court order, or government request, or to protect rights, property, or safety.
              </p>

              <h3 className="text-lg font-medium text-stone-700 mb-2">4.4 Business Transfers</h3>
              <p className="text-stone-600">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred as a business asset.
              </p>
            </section>

            {/* Data Security */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">5. Data Security</h2>
              <p className="text-stone-600 mb-4">
                We implement appropriate security measures to protect your information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-stone-600">
                <li><strong>Encryption:</strong> HTTPS/TLS for data in transit, encryption at rest for sensitive data</li>
                <li><strong>Password Security:</strong> Passwords are hashed using bcrypt</li>
                <li><strong>Access Controls:</strong> Role-based access, authentication required</li>
                <li><strong>Payment Security:</strong> PCI-compliant payment processing via Stripe</li>
                <li><strong>Regular Audits:</strong> Security assessments and monitoring</li>
                <li><strong>Secure Storage:</strong> KYC documents stored securely on Cloudinary</li>
              </ul>
              <p className="text-stone-600 mt-4">
                While we strive to protect your information, no method of transmission or storage is 100% secure. You are responsible for maintaining the confidentiality of your account credentials.
              </p>
            </section>

            {/* Data Retention */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">6. Data Retention</h2>
              <p className="text-stone-600 mb-4">We retain your information for as long as:</p>
              <ul className="list-disc pl-6 space-y-2 text-stone-600">
                <li>Your account is active</li>
                <li>Necessary to provide you with Services</li>
                <li>Required by law (e.g., financial records for 7 years)</li>
                <li>Needed to resolve disputes or enforce agreements</li>
              </ul>
              <p className="text-stone-600 mt-4">
                After account deletion, we may retain anonymized or aggregated data for analytics purposes.
              </p>
            </section>

            {/* Your Rights */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">7. Your Rights</h2>
              <p className="text-stone-600 mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 text-stone-600">
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and data (subject to legal requirements)</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Data Portability:</strong> Request your data in a portable format</li>
                <li><strong>Restrict Processing:</strong> Limit how we use your data in certain circumstances</li>
              </ul>
              <p className="text-stone-600 mt-4">
                To exercise these rights, contact us at <a href={`mailto:${companyEmail}`} className="text-emerald-600 hover:underline">{companyEmail}</a>.
              </p>
            </section>

            {/* Cookies */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">8. Cookies and Tracking Technologies</h2>
              <p className="text-stone-600 mb-4">We use cookies and similar technologies for:</p>
              <ul className="list-disc pl-6 space-y-2 text-stone-600 mb-4">
                <li><strong>Essential Cookies:</strong> Authentication, security, session management</li>
                <li><strong>Functional Cookies:</strong> User preferences, language settings</li>
                <li><strong>Analytics Cookies:</strong> Understanding usage patterns</li>
              </ul>
              <p className="text-stone-600">
                You can control cookies through your browser settings. Disabling certain cookies may affect functionality.
              </p>
            </section>

            {/* Third-Party Links */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">9. Third-Party Links</h2>
              <p className="text-stone-600">
                Our Services may contain links to third-party websites or services. We are not responsible for the privacy practices of these external sites. We encourage you to read their privacy policies.
              </p>
            </section>

            {/* Children's Privacy */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">10. Children's Privacy</h2>
              <p className="text-stone-600">
                Our Services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If we learn that we have collected information from a child, we will delete it promptly.
              </p>
            </section>

            {/* International Users */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">11. International Users</h2>
              <p className="text-stone-600">
                Our Services are primarily operated in Nigeria. If you access our Services from outside Nigeria, your information may be transferred to and processed in Nigeria. By using our Services, you consent to this transfer.
              </p>
            </section>

            {/* Changes to Privacy Policy */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">12. Changes to This Privacy Policy</h2>
              <p className="text-stone-600">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Significant changes may be communicated via email.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">13. Contact Us</h2>
              <p className="text-stone-600 mb-4">
                If you have questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-stone-50 p-4 rounded-lg">
                <p className="text-stone-700 font-medium">LandLordNoAgent - Privacy Team</p>
                <p className="text-stone-600">Email: {companyEmail}</p>
                <p className="text-stone-600">Website: www.landlordnoagent.com</p>
              </div>
            </section>

            {/* Nigerian Data Protection */}
            <section className="mb-8 bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">Nigeria Data Protection Regulation (NDPR)</h2>
              <p className="text-blue-700 mb-4">
                We comply with the Nigeria Data Protection Regulation (NDPR) 2019. As a data controller, we:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-blue-700">
                <li>Process personal data lawfully, fairly, and transparently</li>
                <li>Collect data for specified, explicit, and legitimate purposes</li>
                <li>Ensure data is adequate, relevant, and limited to what is necessary</li>
                <li>Keep data accurate and up to date</li>
                <li>Store data only for as long as necessary</li>
                <li>Implement appropriate security measures</li>
              </ul>
            </section>

            {/* Acknowledgment */}
            <section className="mb-4 bg-emerald-50 p-6 rounded-lg border border-emerald-200">
              <h2 className="text-xl font-semibold text-emerald-800 mb-2">Acknowledgment</h2>
              <p className="text-emerald-700">
                By using LandLordNoAgent, you acknowledge that you have read and understood this Privacy Policy and agree to the collection, use, and disclosure of your information as described.
              </p>
            </section>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link 
            to="/legal/terms" 
            className="px-6 py-3 bg-white border border-stone-200 rounded-xl text-stone-700 hover:border-emerald-300 hover:text-emerald-600 transition-colors font-medium"
          >
            Terms of Service
          </Link>
          <Link 
            to="/help" 
            className="px-6 py-3 bg-white border border-stone-200 rounded-xl text-stone-700 hover:border-emerald-300 hover:text-emerald-600 transition-colors font-medium"
          >
            Help Center
          </Link>
          <Link 
            to="/auth" 
            className="px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium"
          >
            Get Started
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-400 py-8 mt-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm">
            © {new Date().getFullYear()} LandLordNoAgent. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}
